import { useMemo } from 'react';
import { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import type { IAgent } from '../../types/agent';
import type { IFlexiRequest } from '../../types/flexi';
import { formatTimeHandler, formatDuration } from '../../utils';
import { getFlexiStatusColor } from '../flexi-requests/utils';
import { Eye } from 'lucide-react';

interface UseFlexiRequestsColProps {
  onView: (data: IFlexiRequest) => void;
  agents: IAgent[];
}

export const useFlexiRequestsCol = ({
  onView,
  agents,
}: UseFlexiRequestsColProps): ColDef[] => {
  const agentMap = useMemo(
    () => new Map(agents.map((a) => [a.kimId, a.name])),
    [agents]
  );

  const getAgentName = (kimId: string) => agentMap.get(kimId) ?? kimId;

  return [
    {
      headerName: 'Agent',
      field: 'agentName',
      valueGetter: (params: ValueGetterParams<IFlexiRequest>) =>
        params.data?.agentName || getAgentName(params.data?.kimId ?? ''),
      filter: 'agTextColumnFilter',
      minWidth: 150,
    },
    {
      headerName: 'Brand',
      field: 'brandName',
      filter: 'agTextColumnFilter',
      minWidth: 140,
    },
    {
      headerName: 'Login Time',
      field: 'startTime',
      cellRenderer: (params: ICellRendererParams<IFlexiRequest>) =>
        formatTimeHandler(params.data?.startTime, params.data?.timeZone),
      filter: 'agDateColumnFilter',
      minWidth: 180,
    },
    {
      headerName: 'Logout Time',
      field: 'stopTime',
      cellRenderer: (params: ICellRendererParams<IFlexiRequest>) =>
        formatTimeHandler(params.data?.stopTime, params.data?.timeZone),
      filter: 'agDateColumnFilter',
      minWidth: 180,
    },
    {
      headerName: 'Work Duration',
      field: 'workDuration',
      cellRenderer: (params: ICellRendererParams<IFlexiRequest>) =>
        formatDuration(params.value as number),
      filter: 'agNumberColumnFilter',
      minWidth: 130,
    },
    {
      headerName: 'Break Duration',
      field: 'breakDuration',
      cellRenderer: (params: ICellRendererParams<IFlexiRequest>) =>
        formatDuration(params.value as number),
      filter: 'agNumberColumnFilter',
      minWidth: 130,
    },
    {
      headerName: 'Status',
      field: 'status',
      cellRenderer: (params: ICellRendererParams<IFlexiRequest>) => (
        <span
          className={`inline-block px-2 py-1 text-xs rounded ${getFlexiStatusColor(params.value as string, true)}`}
        >
          {params.value}
        </span>
      ),
      filter: 'agTextColumnFilter',
      minWidth: 120,
    },
    {
      headerName: 'Created',
      field: 'createdAt',
      cellRenderer: (params: ICellRendererParams<IFlexiRequest>) =>
        formatTimeHandler(params.data?.createdAt, params.data?.timeZone),
      filter: 'agDateColumnFilter',
      minWidth: 130,
    },
    {
      headerName: 'Actions',
      field: '_id',
      cellRenderer: (params: ICellRendererParams<IFlexiRequest>) => (
        <button
          onClick={() => params.data && onView(params.data)}
          className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
        >
          <Eye size={16} />
          <span>View</span>
        </button>
      ),
      filter: false,
      sortable: false,
      minWidth: 100,
    },
  ];
};
